import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContactsIcon from "@mui/icons-material/Contacts";
import CloseIcon from "@mui/icons-material/Close";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";

import api from "../../../services/api/axios";
import { formatPhone } from "../../../utils/Phone";
import ContactSearchScopeSelect from "../../../shared/components/ContactSearchScopeSelect";
import BrandSpinner from "../../../shared/components/BrandSpinner";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const MAX_CONTACTS = 4;

function normalizeContacts(items = []) {
  const seen = new Set();

  return items.filter((contact) => {
    const email = String(contact?.email || "").trim().toLowerCase();
    const seamlessId = String(contact?.seamless_contact_id || "").trim();
    const name = String(contact?.full_name || "").trim().toLowerCase();
    const key =
      seamlessId ||
      (email ? `email:${email}` : "") ||
      (name ? `name:${contact?.prospecting_company_id || ""}:${name}` : "");

    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function ProspectingCompanyContactsModal({
  open,
  onClose,
  companyId,
  companyName,
  onContactsChanged,
}) {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [enrichingContacts, setEnrichingContacts] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState("");
  const [progressText, setProgressText] = useState("");
  const [searchScope, setSearchScope] = useState("executives");
  const [meta, setMeta] = useState({
    prospectingCompanyId: null,
    enrichmentStatus: null,
    enrichmentError: null,
    lastEnrichedAt: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadContacts = useCallback(async () => {
    if (!open || !companyId) return;

    setLoadingContacts(true);
    setEnrichmentError("");

    try {
      const res = await api.get(`/prospecting-companies/${companyId}/contacts`);
      const payload = res.data || {};
      setContacts(payload.contacts || []);
      setMeta({
        prospectingCompanyId: payload.prospecting_company_id || companyId,
        enrichmentStatus: payload.enrichment_status || null,
        enrichmentError: payload.enrichment_error || null,
        lastEnrichedAt: payload.last_enriched_at || null,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Unable to load saved contacts for this prospecting company.";
      setContacts([]);
      setEnrichmentError(detail);
    } finally {
      setLoadingContacts(false);
    }
  }, [companyId, open]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleEnrich = useCallback(async () => {
    if (!companyId || enrichingContacts) return;

    setEnrichingContacts(true);
    setEnrichmentError("");
    setProgressText("Searching contacts...");
    let progressTimer = null;

    try {
      const request = api.post(
        `/prospecting-companies/${companyId}/enrich-contacts`,
        {
          force_refresh: false,
          search_scope: searchScope,
          max_contacts: MAX_CONTACTS,
        },
        {
          timeout: 60000,
        },
      );

      progressTimer = window.setTimeout(() => {
        setProgressText("Finding & Saving Contacts...");
      }, 700);

      const res = await request;
      const payload = res.data || {};
      setContacts(payload.contacts || []);
      setMeta({
        prospectingCompanyId: payload.prospecting_company_id || companyId,
        enrichmentStatus: payload.enrichment_status || null,
        enrichmentError: payload.enrichment_error || null,
        lastEnrichedAt: payload.last_enriched_at || null,
      });

      const createdCount = payload.created_count || 0;
      if ((payload.contacts || []).length > 0) {
        showSnackbar(
          createdCount > 0
            ? `${createdCount} contacts added`
            : "Contacts refreshed",
        );
      } else {
        showSnackbar("No contacts found for this company.", "info");
      }

      onContactsChanged?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Contact enrichment failed. Please try again.";
      setEnrichmentError(detail);
      showSnackbar(detail, "error");
    } finally {
      if (progressTimer) {
        window.clearTimeout(progressTimer);
      }
      setEnrichingContacts(false);
      setProgressText("");
    }
  }, [companyId, enrichingContacts, onContactsChanged, searchScope, showSnackbar]);

  const dedupedContacts = useMemo(
    () => normalizeContacts(contacts),
    [contacts],
  );

  const lastEnrichedLabel = meta.lastEnrichedAt
    ? new Date(meta.lastEnrichedAt).toLocaleString()
    : null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 620, lg: 760 },
            background:
              "linear-gradient(180deg, rgba(0,51,108,0.06) 0%, rgba(255,255,255,1) 18%)",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            color: "white",
            background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1d4f91 100%)`,
            borderBottom: `3px solid ${BRAND_GREEN}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.25} alignItems="center">
              <ContactsIcon />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Contacts at {companyName || "this company"}
                </Typography>
                {meta.prospectingCompanyId ? (
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Prospecting Company ID: {meta.prospectingCompanyId}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: 3, display: "grid", gap: 2.5, overflowY: "auto" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <ContactSearchScopeSelect
                value={searchScope}
                onChange={(event) => setSearchScope(event.target.value)}
                disabled={enrichingContacts}
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleEnrich}
                disabled={enrichingContacts || !companyId}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: BRAND_BLUE,
                }}
              >
                Find Contacts
              </Button>
            </Stack>
          </Stack>

          {lastEnrichedLabel || meta.enrichmentStatus ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {meta.enrichmentStatus ? (
                <Chip
                  label={`Status: ${meta.enrichmentStatus}`}
                  sx={{
                    width: "fit-content",
                    textTransform: "capitalize",
                    backgroundColor: "rgba(100,131,21,0.12)",
                    color: BRAND_GREEN,
                    fontWeight: 700,
                  }}
                />
              ) : null}
              {lastEnrichedLabel ? (
                <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
                  Last Enriched: {lastEnrichedLabel}
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {loadingContacts ? (
            <BrandSpinner compact label="Loading contacts…" py={8} />
          ) : enrichmentError ? (
            <Alert severity="error">{enrichmentError}</Alert>
          ) : dedupedContacts.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed rgba(0,51,108,0.25)",
                borderRadius: 3,
                px: 3,
                py: 5,
                textAlign: "center",
                backgroundColor: "rgba(255,255,255,0.85)",
              }}
            >
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 0.75 }}>
                No saved contacts yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search for executives and other employees at this company.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                borderRadius: 3,
                border: `1px solid ${BRAND_BLUE}`,
                backgroundColor: "white",
              }}
            >
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f7fb" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: BRAND_BLUE }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: BRAND_BLUE }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: BRAND_BLUE }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: BRAND_BLUE }}>Work Phone</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: BRAND_BLUE }}>Mobile Phone</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: BRAND_BLUE }}>LinkedIn</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dedupedContacts.map((contact) => (
                    <TableRow
                      key={
                        contact.id ||
                        contact.seamless_contact_id ||
                        `${contact.email}-${contact.full_name}`
                      }
                    >
                      <TableCell sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
                        {contact.full_name || "Unknown"}
                      </TableCell>
                      <TableCell sx={{ color: BRAND_BLUE }}>
                        {contact.job_title || "—"}
                      </TableCell>
                      <TableCell>
                        {contact.email ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title={contact.email}>
                              <IconButton
                                component="a"
                                href={`mailto:${contact.email}`}
                                size="small"
                              >
                                <EmailIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy email">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard
                                    .writeText(contact.email)
                                    .then(() => showSnackbar("Email copied"))
                                    .catch(() =>
                                      showSnackbar("Unable to copy email.", "error"),
                                    );
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={{ color: BRAND_BLUE }}>
                        {contact.work_phone || contact.phone ? (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhoneIcon sx={{ color: BRAND_GREEN, fontSize: 18 }} />
                            <span>{formatPhone(contact.work_phone || contact.phone)}</span>
                          </Stack>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={{ color: BRAND_BLUE }}>
                        {contact.mobile_phone ? (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhoneIcon sx={{ color: BRAND_GREEN, fontSize: 18 }} />
                            <span>{formatPhone(contact.mobile_phone)}</span>
                          </Stack>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.linkedin_url || contact.linkedin ? (
                          <Tooltip title="Open LinkedIn">
                            <IconButton
                              component="a"
                              href={contact.linkedin_url || contact.linkedin}
                              target="_blank"
                              rel="noreferrer"
                              size="small"
                            >
                              <LinkedInIcon sx={{ color: "#0A66C2" }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {enrichingContacts ? (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: BRAND_BLUE, fontWeight: 600 }}>
                {progressText || "Searching contacts..."}
              </Typography>
            </Stack>
          ) : null}

          {meta.enrichmentError && !enrichmentError ? (
            <Alert severity="warning">{meta.enrichmentError}</Alert>
          ) : null}
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
